import React from 'react';
import { useState } from 'react';
import { formatDateForDisplay } from '../../utils/dateUtils';

import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Paper,
  Typography,
  Button,
  Box,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Transaction } from '../../types/Transaction';
import { useAuth } from '../../context/AuthContext';
import { getUiSignedAmount } from '../../utils/transactionAmount';
import { useFormatting } from '../../context/FormattingContext';
import { formatMoney } from '../../utils/formatting';
import { authFetch } from '../../lib/authFetch';

import CircularProgress from '@mui/material/CircularProgress';

interface TransactionTableProps {
  transactions: Transaction[];
  onView?: (tx: Transaction) => void;
  onEdit?: (tx: Transaction) => void;
  onDelete?: (tx: Transaction) => void;
  showFuture?: boolean;
  onRefresh?: () => void; // ✅ Add this line
  loading?: boolean;
}



const TransactionTable: React.FC<TransactionTableProps> = ({
  transactions,
  onView,
  onEdit,
  onDelete,
  showFuture = false, // ✅ Destructure it with a default fallback
  onRefresh, // ✅ Destructure here
  loading = false,


}) => {

  const { auth } = useAuth();
  const formatting = useFormatting();
  const formatSignedAmount = (value: number, isPositive: boolean): string => {
    const absolute = formatMoney(Math.abs(value), formatting);
    if (absolute === '—') return '—';
    return `${isPositive ? '+' : '−'}${absolute}`;
  };

  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const getKindLabel = (tx: Transaction): string => {
    const kindOrType = tx.kind || tx.type;
    if (kindOrType === 'income') return 'Income';
    if (kindOrType === 'expense') return 'Expense';
    if (kindOrType === 'transfer') return 'Transfer';
    if (kindOrType === 'cardPurchase') return 'Card Purchase';
    if (kindOrType === 'cardPayment') return 'Card Payment';
    if (kindOrType === 'adjustment') return 'Adjustment';
    if (kindOrType === 'savings') return 'Savings';
    return kindOrType || '—';
  };

  const handleConfirm = async (tx: Transaction) => {
    setConfirmingId(tx._id);
    try {
      const res = await authFetch(`/api/ledger/transactions/${tx._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth?.token}`,
        },
        body: JSON.stringify({ tentative: false }),
      });

      if (!res.ok) throw new Error('Failed to confirm transaction');
      onRefresh?.();
    } catch (err) {
      console.error('Confirm error:', err);
    } finally {
      setConfirmingId(null);
    }
  };


  return (
    <Paper elevation={2} sx={{ p: { xs: 1.25, sm: 2 } }}>
      <Typography variant="h6" gutterBottom>
        <span style={{ fontWeight: 'bold' }}>Transactions</span>
      </Typography>
      <TableContainer data-testid="tx-table-scroll-container" sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 880 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ textDecoration: 'underline', fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1rem' }, whiteSpace: 'nowrap', px: { xs: 1, sm: 2 } }}>
                Date Posted
              </TableCell>
              <TableCell sx={{ textDecoration: 'underline', fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1rem' }, whiteSpace: 'nowrap', px: { xs: 1, sm: 2 } }}>
                Description
              </TableCell>
              <TableCell sx={{ textDecoration: 'underline', fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1rem' }, whiteSpace: 'nowrap', px: { xs: 1, sm: 2 } }}>
                Movement
              </TableCell>
              <TableCell sx={{ textDecoration: 'underline', fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1rem' }, whiteSpace: 'nowrap', px: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>
                Kind
              </TableCell>
              <TableCell sx={{ textDecoration: 'underline', fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1rem' }, whiteSpace: 'nowrap', px: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>
                Category
              </TableCell>
              <TableCell sx={{ textDecoration: 'underline', fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1rem' }, whiteSpace: 'nowrap', px: { xs: 1, sm: 2 } }}>
                Transaction
              </TableCell>
              <TableCell
                align="right"
                sx={{ textDecoration: 'underline', fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1rem' }, whiteSpace: 'nowrap', px: { xs: 1, sm: 2 } }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...transactions]
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
              .map((tx: Transaction) => {
                const isFuture = new Date(tx.date) > new Date();
                const fallbackPositive = tx.type === 'income';
                const resolvedSigned = getUiSignedAmount(tx);
                const hasSignedAmount = typeof resolvedSigned === 'number';
                const signed = hasSignedAmount ? resolvedSigned : 0;
                const isPositive = hasSignedAmount ? signed >= 0 : fallbackPositive;
                const displayAmount = hasSignedAmount
                  ? formatSignedAmount(signed, isPositive)
                  : formatSignedAmount(tx.amount, isPositive);
                const direction = tx.direction || {
                  fromAccountName: tx.fromAccountName,
                  toAccountName: tx.toAccountName,
                };
                const movementLabel =
                  (tx.kind === 'transfer' || tx.kind === 'cardPayment') &&
                  direction.fromAccountName &&
                  direction.toAccountName
                    ? `${direction.fromAccountName} → ${direction.toAccountName}`
                    : undefined;

                return (
                  <TableRow
                    key={tx._id}
                    data-testid={`tx-row-${tx._id}`}
                    sx={{
                      ...(isFuture && {
                        backgroundColor: 'rgba(255, 235, 59, 0.1)', // light yellow tint
                        opacity: 0.8,
                      }),
                      ...(tx.tentative && {
                        fontStyle: 'italic',
                      }),
                    }}
                  >

                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1.25 }, whiteSpace: 'nowrap' }}>
                      {tx.date ? formatDateForDisplay(tx.date) : '—'}
                    </TableCell>

                    <TableCell data-testid={`tx-description-${tx._id}`} sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1.25 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 140, maxWidth: 220 }}>
                        <Typography noWrap title={tx.description || '-'} sx={{ fontSize: { xs: '0.82rem', sm: '0.95rem' } }}>
                          {tx.description || '-'}
                        </Typography>
                        {tx.tentative && (
                          <Chip
                            data-testid={`tx-tentative-${tx._id}`}
                            label="What-if"
                            color="warning"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1.25 } }}>
                      {movementLabel ? (
                        <Typography
                          data-testid={`tx-direction-${tx._id}`}
                          sx={{ fontWeight: 500, minWidth: 160, maxWidth: 260, whiteSpace: 'normal', lineHeight: 1.3, fontSize: { xs: '0.82rem', sm: '0.95rem' } }}
                        >
                          {movementLabel}
                        </Typography>
                      ) : (
                        <Typography sx={{ color: 'text.secondary' }}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell data-testid={`tx-kind-${tx._id}`} sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1.25 }, display: { xs: 'none', sm: 'table-cell' } }}>{getKindLabel(tx)}</TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1.25 }, display: { xs: 'none', sm: 'table-cell' } }}>{tx.category}</TableCell>
                    <TableCell data-testid={`tx-amount-${tx._id}`} sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1.25 }, whiteSpace: 'nowrap' }}>
                      <Typography
                        sx={{ color: isPositive ? 'green' : 'error.main', fontWeight: 600, fontSize: { xs: '0.82rem', sm: '0.95rem' } }}
                      >
                        {displayAmount}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1.25 } }}>
                      <Box data-testid={`tx-actions-${tx._id}`} sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: 0.25, minWidth: 112 }}>
                        {loading && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                            <CircularProgress size={20} />
                          </Box>
                        )}
                        {tx.tentative && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            onClick={() => handleConfirm(tx)}
                            disabled={confirmingId === tx._id} // ✅ Disable during processing
                            sx={{ px: 0.75, minWidth: 0 }}
                          >
                            {confirmingId === tx._id ? 'Confirming…' : 'Confirm'}
                          </Button>
                        )}

                        <IconButton data-testid={`tx-view-${tx._id}`} onClick={() => onView?.(tx)} size="small">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton data-testid={`tx-edit-${tx._id}`} onClick={() => onEdit?.(tx)} size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          data-testid={`tx-delete-${tx._id}`}
                          sx={{ color: 'black' }}
                          size="small"
                          onClick={() => onDelete?.(tx)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>

        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TransactionTable;
